import { useState, useEffect, useRef } from 'react';
import { ModelResponse } from '../types';
import { ConnectionState } from '../services/WebSocketManager';
import { apiService } from '../services/ApiService';

export interface UseFallbackPollingOptions {
  demoRunId: string | null;
  isRunning: boolean;
  connectionState: ConnectionState;
  currentResponses: Record<string, ModelResponse>;
}

const POLL_INTERVAL_MS = 3000;

/**
 * Reconcile polled responses with current (WS-accumulated) responses.
 * For each model, keep the longer responseText to avoid losing data.
 */
export function reconcileResponses(
  polled: Record<string, ModelResponse>,
  current: Record<string, ModelResponse>
): Record<string, ModelResponse> {
  const result: Record<string, ModelResponse> = {};
  const allModelIds = Array.from(new Set([...Object.keys(polled), ...Object.keys(current)]));

  for (const modelId of allModelIds) {
    const polledModel = polled[modelId];
    const currentModel = current[modelId];

    if (!polledModel) {
      result[modelId] = currentModel;
    } else if (!currentModel) {
      result[modelId] = polledModel;
    } else {
      const usePolled = polledModel.responseText.length >= currentModel.responseText.length;
      result[modelId] = usePolled ? polledModel : currentModel;
    }
  }

  return result;
}

export function useFallbackPolling(
  options: UseFallbackPollingOptions
): { fallbackResponses: Record<string, ModelResponse> | null } {
  const { demoRunId, isRunning, connectionState, currentResponses } = options;
  const [fallbackResponses, setFallbackResponses] = useState<Record<string, ModelResponse> | null>(
    null
  );
  const currentResponsesRef = useRef(currentResponses);
  currentResponsesRef.current = currentResponses;

  const shouldPoll = connectionState === 'fallback' && isRunning && demoRunId != null;

  useEffect(() => {
    if (!shouldPoll || !demoRunId) {
      setFallbackResponses(null);
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const demoRun = await apiService.getDemoRunDetail(demoRunId);
        if (cancelled) return;
        const reconciled = reconcileResponses(demoRun.responses, currentResponsesRef.current);
        setFallbackResponses(reconciled);
      } catch (error) {
        // Log and retry on next interval (Requirement: retry on next 3-second interval)
        console.error('Fallback polling error:', error);
      }
    };

    // Poll immediately, then every 3 seconds
    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [shouldPoll, demoRunId]);

  return { fallbackResponses };
}
